import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { SpeakButton } from "@/components/SpeakButton";
import MilestoneCelebration, { MILESTONES } from "@/components/MilestoneCelebration";
import { GraduationCap, Sparkles, Flame, Check, Share2 } from "lucide-react";

function RootCard({ r, highlight }) {
  const shareRoot = async () => {
    const url = `${window.location.origin}/roots`;
    const text = `${r.root} (${r.language}) — "${r.meaning}"\n\nWords it builds: ${r.examples.join(", ")}\n\n"${r.affirmation}"\n\nA root mini-lesson from Mirrors of Your Soul`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `The root "${r.root}"`, text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Root lesson copied — share it with a friend.");
      }
    } catch (err) {
      if (err && err.name === "AbortError") return; // user cancelled the share sheet
      try {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Root lesson copied — share it with a friend.");
      } catch {
        toast.error("Couldn't share automatically — copy this page's link to share.");
      }
    }
  };

  return (
    <div
      data-testid={`root-card-${r.root}`}
      className={`glass rounded-2xl p-8 ${highlight ? "gold-glow border-[#D4AF37]/40" : ""}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="serif text-4xl text-[#F5F1E7]">{r.root}</h3>
        <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-3 py-1 whitespace-nowrap">
          {r.language}
        </span>
      </div>
      <p className="mt-3 text-[#A8A29E]">
        <span className="text-[#F5F1E7]">meaning:</span> {r.meaning}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {r.examples.map((e, i) => (
          <span key={i} className="text-sm px-3 py-1 rounded-full border border-white/10 text-[#F5F1E7]">
            {e}
          </span>
        ))}
      </div>
      <p className="mt-5 text-[#A8A29E] leading-relaxed italic-serif text-lg">{r.reflection}</p>
      <div className="mt-5 border-t border-white/5 pt-5">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Speak it over yourself</p>
        <p className="text-[#F5F1E7] text-xl italic-serif leading-relaxed">“{r.affirmation}”</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <SpeakButton text={r.affirmation} word={r.root} record label="Speak it aloud" />
          <button
            data-testid={`share-root-${r.root}`}
            onClick={shareRoot}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 text-[#A8A29E] px-5 py-2 text-sm hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300"
          >
            <Share2 size={15} /> Share this root
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Roots() {
  const { user } = useAuth();
  const [roots, setRoots] = useState([]);
  const [daily, setDaily] = useState(null);
  const [streak, setStreak] = useState(null);
  const [milestone, setMilestone] = useState(null);

  useEffect(() => {
    api.get("/roots").then((r) => setRoots(r.data.roots || []));
    api.get("/roots/daily").then((r) => setDaily(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) api.get("/roots/streak").then((r) => setStreak(r.data)).catch(() => {});
    else setStreak(null);
  }, [user]);

  const markLearned = async () => {
    try {
      const r = await api.post("/roots/learn");
      setStreak({ ...r.data });
      if (MILESTONES.includes(r.data.streak)) {
        setMilestone(r.data.streak);
      } else {
        toast.success(`Root learned — ${r.data.streak} day streak.`);
      }
    } catch {
      toast.error("Could not save your streak.");
    }
  };

  return (
    <main data-testid="roots-page" className="max-w-6xl mx-auto px-6 py-16">
      <MilestoneCelebration milestone={milestone} onClose={() => setMilestone(null)} />
      <div className="max-w-3xl mb-12 fade-up">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          <GraduationCap size={14} /> Roots Mini-Lessons
        </p>
        <h1 className="serif text-4xl sm:text-6xl font-light text-[#F5F1E7]">
          Rebuild what the schools <span className="italic-serif text-[#D4AF37]">dropped.</span>
        </h1>
        <p className="mt-5 text-[#A8A29E] leading-relaxed text-lg">
          A Latin or Greek root each day. Learn the small pieces that build a thousand words — and
          speak them back over your life.
        </p>
      </div>

      {/* Streak */}
      <div className="mb-10 glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="root-streak">
        <div className="flex items-center gap-4">
          <span className="h-12 w-12 rounded-full bg-[#D4AF37]/10 grid place-items-center text-[#D4AF37]">
            <Flame size={22} />
          </span>
          <div>
            {user ? (
              <>
                <p className="serif text-3xl text-[#F5F1E7]" data-testid="streak-count">
                  {streak ? streak.streak : 0} day{(streak?.streak || 0) === 1 ? "" : "s"} in a row
                </p>
                <p className="text-sm text-[#A8A29E]">
                  Longest streak: {streak?.longest || 0}
                  {streak?.learned_today ? " · learned today ✓" : ""}
                </p>
                {(() => {
                  const cur = streak?.streak || 0;
                  const next = MILESTONES.find((m) => m > cur);
                  if (!next) return null;
                  return (
                    <p className="text-xs text-[#D4AF37] mt-1" data-testid="next-milestone">
                      {next - cur} day{next - cur === 1 ? "" : "s"} to your {next}-day blessing
                    </p>
                  );
                })()}
              </>
            ) : (
              <p className="text-[#A8A29E]">
                <Link to="/login" className="text-[#D4AF37] hover:underline">Sign in</Link> to keep a daily streak.
              </p>
            )}
          </div>
        </div>
        {user && (
          <button
            data-testid="mark-learned-btn"
            onClick={markLearned}
            disabled={streak?.learned_today}
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#110D14] px-6 py-2.5 font-medium hover:bg-[#e6c860] transition-colors duration-300 disabled:opacity-50"
          >
            <Check size={16} /> {streak?.learned_today ? "Learned today" : "Mark as learned today"}
          </button>
        )}
      </div>

      {daily && (
        <section className="mb-14" data-testid="root-of-day">
          <div className="flex items-center gap-2 mb-4 text-[#D4AF37]">
            <Sparkles size={16} />
            <span className="text-xs uppercase tracking-[0.25em]">Root of the day · {daily.index + 1} of {daily.total}</span>
          </div>
          <RootCard r={daily.root} highlight />
        </section>
      )}

      <h2 className="serif text-3xl text-[#F5F1E7] mb-6">The full library</h2>
      <div className="grid md:grid-cols-2 gap-6" data-testid="roots-library">
        {roots.map((r) => (
          <RootCard key={r.root} r={r} />
        ))}
      </div>
    </main>
  );
}
